import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useOperatorActions } from "../../../../utils/useOperatorActions";
import CyberBoxComponent from "../../../shared/ui/layout/CyberBoxComponent";
import QuestionComponent from "./components/questions/questionComponent";
import WelcomeComponent from "../day-1/components/welcomeComponent";
import BullMastersDisplayComponent from "./components/teams/BullMastersDisplayComponent";
import GoldenEaglesDisplayComponent from "./components/teams/GoldenEaglesDisplayComponent";
import AlphaWolvesDisplayComponent from "./components/teams/AlphaWolvesDisplayComponent";
import IronBearsDisplayComponent from "./components/teams/IronBearsDisplayComponent";
import { QuestionsResultTableComponent } from "./components/questions/questionsResultTableComponent";
import DraftingComponent from "./components/drafting/draftingComponentNew";
import BoxComponent from "./components/box/boxComponent";
import QuestionsIntroComponent from "./components/questionsIntroComponent";

export function Day2() {
  const [currentHtml, setCurrentHtml] = useState(null);

  // reveal state for welcome → table
  const [canReveal, setCanReveal] = useState(false);
  const [showTablesNow, setShowTablesNow] = useState(false);
  const [tablesSessionKey, setTablesSessionKey] = useState(0);
  const revealTimerRef = useRef(null);

  const TABLE_DELAY_MS = 3000;
  const reduceMotion = useReducedMotion();

  const clearRevealTimer = () => {
    if (revealTimerRef.current) {
      clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  };

  // --- Operator Actions ---
  useOperatorActions("SHOW_AHANGARI_HOLDING", () => {
    setCurrentHtml("AHANGARI_HOLDING_LOGO_DISPLAY");
  });
  useOperatorActions("SHOW_IRONBEARS", () => {
    setCurrentHtml("SHOW_IRONBEARS");
  });
  useOperatorActions("SHOW_BULLMASTERS", () => {
    setCurrentHtml("SHOW_BULLMASTERS");
  });
  useOperatorActions("SHOW_ALPHAWOLVES", () => {
    setCurrentHtml("SHOW_ALPHAWOLVES");
  });
  useOperatorActions("SHOW_GOLDENEAGLES", () => {
    setCurrentHtml("SHOW_GOLDENEAGLES");
  });
  useOperatorActions("DAY2_SHOW_QUESTIONS", () => {
    setCurrentHtml("DAY2_SHOW_QUESTIONS");
  });
  useOperatorActions("SHOW_CAPTAINS_DRAFT", () => {
    setCurrentHtml("SHOW_CAPTAINS_DRAFT");
  });
  useOperatorActions("SHOW_BOX", () => {
    setCurrentHtml("SHOW_BOX");
  });
  useOperatorActions("SHOW_INTRO", () => {
    setCurrentHtml("SHOW_INTRO");
  });
  // NEW: wrap result table in welcome flow
  const startTableReveal = () => {
    clearRevealTimer();
    setTablesSessionKey((k) => k + 1);
    setCanReveal(false);
    setShowTablesNow(false);
    setCurrentHtml("SHOW_QUESTIONS_RESULT_TABLE");

    revealTimerRef.current = setTimeout(() => {
      setCanReveal(true); // triggers welcome exit
      revealTimerRef.current = null;
    }, TABLE_DELAY_MS);
  };
  useOperatorActions("SHOW_QUESTIONS_RESULT_TABLE", startTableReveal);

  useEffect(() => () => clearRevealTimer(), []);

  // --- Animations ---
  const variants = {
    initial: { opacity: 0, scale: 0.96, y: 12, filter: "blur(6px)" },
    animate: { opacity: 1, scale: 1, y: 0, filter: "blur(0px)" },
    exit: { opacity: 0, scale: 0.98, y: -8, filter: "blur(3px)" },
  };

  // helper to render a layer with welcome overlay
  const renderTableLayer = (children, welcomeText) => (
    <div className="relative w-full flex items-center justify-center">
      {/* TABLE (hidden until welcome exits) */}
      <motion.div
        key={`tables-${tablesSessionKey}`}
        initial={{ opacity: 0, y: 8, scale: 0.98, filter: "blur(3px)" }}
        animate={
          showTablesNow
            ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
            : { opacity: 0, y: 8, scale: 0.98, filter: "blur(3px)" }
        }
        transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
        aria-hidden={!showTablesNow}
        className={`w-full flex justify-center ${
          showTablesNow ? "pointer-events-auto" : "pointer-events-none"
        }`}
        style={
          showTablesNow
            ? {}
            : {
                position: "absolute",
                inset: 0,
                visibility: "hidden",
              }
        }
      >
        {children}
      </motion.div>

      {/* WELCOME OVERLAY */}
      <AnimatePresence
        onExitComplete={() => setShowTablesNow(true)}
        mode="wait"
      >
        {!canReveal && (
          <motion.div
            key="welcome-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <WelcomeComponent text={welcomeText} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // --- Render Switch ---
  const renderHtml = (html) => {
    switch (html) {
      case "SHOW_INTRO":
        return <QuestionsIntroComponent />;
      case "SHOW_GOLDENEAGLES":
        return <GoldenEaglesDisplayComponent />;
      case "SHOW_ALPHAWOLVES":
        return <AlphaWolvesDisplayComponent />;
      case "SHOW_BULLMASTERS":
        return <BullMastersDisplayComponent />;
      case "SHOW_IRONBEARS":
        return <IronBearsDisplayComponent />;
      case "DAY2_SHOW_QUESTIONS":
        return <QuestionComponent />;
      case "SHOW_QUESTIONS_RESULT_TABLE":
        return renderTableLayer(
          <QuestionsResultTableComponent />,
          "رده بندی نهایی پرسش و پاسخ"
        );
      case "SHOW_CAPTAINS_DRAFT":
        return <DraftingComponent />;
      case "SHOW_BOX":
        return <BoxComponent />;
      default:
        return <WelcomeComponent />;
    }
  };

  return (
    <CyberBoxComponent>
      <div className="w-screen h-screen flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentHtml ?? "empty"}
            variants={reduceMotion ? undefined : variants}
            initial={reduceMotion ? false : "initial"}
            animate={reduceMotion ? undefined : "animate"}
            exit={reduceMotion ? undefined : "exit"}
            className="w-screen px-8"
            transition={{ duration: 0.6 }}
          >
            {renderHtml(currentHtml)}
          </motion.div>
        </AnimatePresence>
      </div>
    </CyberBoxComponent>
  );
}

export default Day2;
